import { resolveUiHelper } from '../utils/resolve-ui-helper.js';


const DEFAULT_STATE = 'empty';

export class SearchView {
  constructor({
    document = typeof window !== 'undefined' ? window.document : null,
    modalFactory = null,
    escape = null,
  } = {}) {
    this.document = document;
    this.hasExternalModalFactory = typeof modalFactory === 'function';
    let helper = null;
    if (!this.hasExternalModalFactory || typeof escape !== 'function') {
      helper = resolveUiHelper() || {};
    }
    const resolvedModalFactory =
      this.hasExternalModalFactory
        ? modalFactory
        : typeof helper?.makeModal === 'function'
        ? helper.makeModal
        : typeof window !== 'undefined' && typeof window.makeModal === 'function'
        ? window.makeModal
        : null;
    const resolvedEscape =
      typeof escape === 'function'
        ? escape
        : typeof helper?.esc === 'function'
        ? helper.esc
        : typeof window !== 'undefined' && typeof window.esc === 'function'
        ? window.esc
        : null;
    this.modalFactory = this.hasExternalModalFactory ? resolvedModalFactory : null;
    this.fallbackModalFactory = this.hasExternalModalFactory ? null : resolvedModalFactory;
    this.escape = typeof resolvedEscape === 'function' ? resolvedEscape : (value) => value;
    this.modal = null;
    this.handlers = null;
    this.boundListeners = [];
    this.elements = {
      input: null,
      results: null,
      empty: null,
      loading: null,
      noResults: null,
      list: null,
      searchButton: null,
    };
  }

  mount({ onOpen, onQueryChange, onSubmit, onResultSelect } = {}) {
    this.destroy();
    this.handlers = { onOpen, onQueryChange, onSubmit, onResultSelect };

    if (!this.document) {
      return;
    }

    this.elements = {
      input: this.document.getElementById('search-input'),
      results: this.document.getElementById('search-results'),
      empty: this.document.getElementById('search-empty'),
      loading: this.document.getElementById('search-loading'),
      noResults: this.document.getElementById('search-no-results'),
      list: this.document.getElementById('search-list'),
      searchButton: this.document.getElementById('sb-search-chats'),
    };

    if (this.hasExternalModalFactory && this.modalFactory) {
      try {
        this.modal = this.modalFactory('search');
      } catch (error) {
        this.modal = null;
      }
    }

    if (this.elements.input && typeof onQueryChange === 'function') {
      const handler = (event) => {
        onQueryChange(event?.target?.value ?? '');
      };
      this.elements.input.addEventListener('input', handler);
      this.boundListeners.push({ element: this.elements.input, type: 'input', handler });
    }

    if (this.elements.input && typeof onSubmit === 'function') {
      const handler = (event) => {
        if (event?.key === 'Enter') {
          onSubmit(event.target?.value ?? '');
        }
      };
      this.elements.input.addEventListener('keydown', handler);
      this.boundListeners.push({ element: this.elements.input, type: 'keydown', handler });
    }

    if (this.elements.searchButton && typeof onOpen === 'function') {
      const handler = (event) => {
        event?.preventDefault?.();
        onOpen();
      };
      this.elements.searchButton.addEventListener('click', handler);
      this.boundListeners.push({ element: this.elements.searchButton, type: 'click', handler });
    }

    this.showState(DEFAULT_STATE);
  }

  destroy() {
    this.boundListeners.forEach(({ element, type, handler }) => {
      element?.removeEventListener(type, handler);
    });
    this.boundListeners = [];
    this.handlers = null;
  }

  openModal() {
    this.#ensureModal();
    if (this.modal && typeof this.modal.open === 'function') {
      this.modal.open();
    }
    this.clearInput();
    this.showState(DEFAULT_STATE);
    this.focusInput();
  }

  closeModal() {
    if (this.modal && typeof this.modal.close === 'function') {
      this.modal.close();
    }
  }

  focusInput() {
    this.elements.input?.focus?.();
  }

  clearInput() {
    if (this.elements.input) {
      this.elements.input.value = '';
    }
  }

  showState(state) {
    const map = {
      empty: this.elements.empty,
      loading: this.elements.loading,
      'no-results': this.elements.noResults,
      results: this.elements.results,
    };
    Object.entries(map).forEach(([key, el]) => {
      if (!el) {
        return;
      }
      if (key === 'results') {
        el.hidden = state !== 'results';
      } else {
        el.style.display = key === state ? '' : 'none';
      }
    });
  }

  #ensureModal() {
    if (this.modal) {
      return;
    }

    if (!this.modalFactory && typeof window !== 'undefined' && typeof window.makeModal === 'function') {
      this.modalFactory = window.makeModal;
    }

    if (!this.modalFactory) {
      if (typeof this.fallbackModalFactory === 'function') {
        this.modalFactory = this.fallbackModalFactory;
      } else {
        const helper = resolveUiHelper() || {};
        if (typeof helper.makeModal === 'function') {
          this.modalFactory = helper.makeModal;
          this.fallbackModalFactory = helper.makeModal;
        }
      }
    }

    if (!this.modalFactory && typeof window !== 'undefined' && typeof window.makeModal === 'function') {
      this.modalFactory = window.makeModal;
    }

    if (typeof this.modalFactory !== 'function') {
      return;
    }

    try {
      this.modal = this.modalFactory('search');
    } catch (error) {
      this.modal = null;
    }
  }

  renderResults(results) {
    const list = this.elements.list;
    if (!list) {
      return;
    }
    list.innerHTML = '';

    const normalized = Array.isArray(results) ? results : [];
    normalized.forEach((result) => {
      const item = this.#createResultElement(result);
      list.appendChild(item);
    });
  }

  #createResultElement(result) {
    const div = this.document.createElement('div');
    div.className = 'search-result-item';
    div.style.cssText = [
      'padding: 12px 16px',
      'border-bottom: 1px solid var(--line)',
      'cursor: pointer',
      'transition: background-color 0.2s',
    ].join(';');

    const projectTitle = this.escape(result?.projectTitle || 'Unfiled');
    const chatTitle = this.escape(result?.title || 'Untitled Chat');
    const excerpt = this.escape(result?.excerpt || '');

    div.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
        <span style="font-size: 12px; color: var(--ink-dim); background: rgba(255,255,255,0.1); padding: 2px 6px; border-radius: 12px;">${projectTitle}</span>
        <span style="font-weight: 500; color: var(--ink);">${chatTitle}</span>
      </div>
      ${excerpt ? `<div style="font-size: 14px; color: var(--ink-dim); line-height: 1.4;">${excerpt}</div>` : ''}
    `;

    div.addEventListener('mouseenter', () => {
      div.style.backgroundColor = 'var(--hover)';
    });
    div.addEventListener('mouseleave', () => {
      div.style.backgroundColor = 'transparent';
    });
    div.addEventListener('click', () => {
      const handler = this.handlers?.onResultSelect;
      if (typeof handler === 'function') {
        handler(result?.id || null, result);
      }
      this.closeModal();
    });

    return div;
  }
}

if (typeof window !== 'undefined') {
  window.AgileProViews = window.AgileProViews || {};
  window.AgileProViews.SearchView = SearchView;
}
